/**
 * IstAttributeSection — the branchenspezifischen Ist-Attribute editor for ONE insured object
 * (P2 Placement Slice 2, Ausschreibung Ch 5). Reuses the Slice-1 RisikoprofilFeldInput with the
 * Police `optionLabel` resolver. Read mode shows the values + "x/N Sparte gespeichert"; edit mode
 * gathers a merge-patch and PATCHes on save. For a 91000 Einzelleben policy on a Privatperson the
 * gated Gesundheitspruefung-Status is shown as a read-only MIRROR row (never editable here, D2).
 *
 * Behind the `policen.risiko_deckung.ist` Kachel visibility key.
 */

import { useState } from 'react';
import {
  GESUNDHEITSPRUEFUNG_STATUS_LABELS,
  policeRisikoOptionLabel,
} from '@brokly/shared';
import type {
  BranchenFeldDefinition,
  GesundheitspruefungStatus,
  PoliceRisikoObjekt,
  PoliceRisikoPatchBody,
} from '@brokly/shared';
import { Button } from '../../../../components/ui/Button';
import { RisikoprofilFeldInput } from '../risikoprofil/RisikoprofilFeldInput';

export function IstAttributeSection({
  felder,
  objekt,
  canWrite,
  isSaving,
  onPatch,
  gesundheitspruefungStatus,
  gatedSuppressed,
}: {
  felder: BranchenFeldDefinition[];
  objekt: PoliceRisikoObjekt;
  canWrite: boolean;
  isSaving: boolean;
  onPatch: (body: PoliceRisikoPatchBody) => void;
  /** 91000 mirror value (present only when visible). */
  gesundheitspruefungStatus?: GesundheitspruefungStatus | null;
  /** gated keys suppressed on this read (role/consent). */
  gatedSuppressed: string[];
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  const values: Record<string, unknown> = { ...objekt.attributes, ...draft };
  const title = objekt.insurance_object_ref === null ? 'Police allgemein' : objekt.object_label ?? objekt.insurance_object_ref;

  function save() {
    // Only the touched keys travel (a true merge-patch; absent keeps, null clears).
    onPatch({ insurance_object_ref: objekt.insurance_object_ref, attributes: draft });
    setEditing(false);
    setDraft({});
  }

  const showGesundheitMirror = gesundheitspruefungStatus !== undefined || gatedSuppressed.includes('gesundheitspruefung_status');

  return (
    <section
      aria-labelledby={`pr-ist-${objekt.insurance_object_ref ?? 'policy'}`}
      className="rounded-lg border bg-card p-4"
      data-testid={`pr-ist-${objekt.insurance_object_ref ?? 'policy'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h4 id={`pr-ist-${objekt.insurance_object_ref ?? 'policy'}`} className="text-sm font-semibold text-foreground">
            {title}
          </h4>
          <span className="text-xs text-muted-foreground" data-testid="pr-ist-count">
            {objekt.filled}/{objekt.total} Sparte gespeichert
          </span>
        </div>
        {canWrite &&
          (editing ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft({}); }} disabled={isSaving}>
                Abbrechen
              </Button>
              <Button size="sm" onClick={save} disabled={isSaving}>
                {isSaving ? 'Speichern…' : 'Speichern'}
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="pr-ist-edit">
              Bearbeiten
            </Button>
          ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {felder.map((def) => (
          <RisikoprofilFeldInput
            key={def.key}
            def={def}
            value={values[def.key] ?? null}
            editing={editing}
            disabled={isSaving}
            optionLabel={policeRisikoOptionLabel}
            onChange={(v) => setDraft((d) => ({ ...d, [def.key]: v }))}
          />
        ))}
      </div>

      {showGesundheitMirror && (
        <div className="mt-3 rounded-md border border-dashed bg-muted/30 p-3" data-testid="pr-ist-gesundheit-mirror">
          <span className="text-xs font-medium text-muted-foreground">
            Gesundheitspruefung (Mirror aus Kundenprofil)
          </span>
          <div className="text-sm text-foreground">
            {gatedSuppressed.includes('gesundheitspruefung_status')
              ? 'Gesperrt (fehlende Berechtigung)'
              : gesundheitspruefungStatus
                ? GESUNDHEITSPRUEFUNG_STATUS_LABELS[gesundheitspruefungStatus]
                : '-'}
          </div>
        </div>
      )}
    </section>
  );
}
